/**
 * page-orgs — the organization list and the create form, routed at `/orgs`.
 *
 * The organization is the tenancy root (`concept.md` §5.4): it owns instances,
 * datasources, policy objects, generations, ingested audit and billing. Nothing
 * in this product is global, which is why this list is the first screen a new
 * account can meaningfully reason about.
 *
 * Implements:
 * - `SC-F100` — organization model: create a shared organization, owner is the
 *   caller (`SC-S100`).
 * - `SC-F101` — the **personal organization**, created for the account at
 *   sign-up. It is marked as such here, because "why do I already have one" is
 *   the first question this list raises.
 * - `SC-F109` — the caller's organization list, with the caller's own role on
 *   every row. This is the list the chrome's switcher is built from (`SC-S101`).
 * - `SC-F901` — a create refused by the plan's organization quota renders the
 *   `{quota, limit, current}` triple rather than a bare "forbidden".
 *
 * Four states, all of them real: loading, empty, failed (rendering the `apierr`
 * code, message and `request_id` so a reader can quote it in a support ticket)
 * and populated.
 */

import { LitElement, css, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import {
  ROLE_LABELS,
  createOrg,
  listOrgs,
  type Organization,
  type Role,
} from "../api/orgs.js";
import { ApiError, parseError, validationFields, type QuotaDetail } from "../api/errors.js";
import { navigate } from "../nav.js";
import { toast, type BnBadgeVariant, type BnColumn, type BnKvRow, type BnRow } from "../components/index.js";

/**
 * The RFC-1123 label the API stores as the organization's `name`: lowercase
 * alphanumerics and hyphens, starting and ending on an alphanumeric. It appears
 * in URLs and in generated hostnames (`05` §3.3), so it is validated before the
 * round trip rather than only after it.
 */
const LABEL_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
const LABEL_MAX = 63;

/** roleVariant keeps a role's tone identical on every screen that shows one. */
function roleVariant(role: Role | undefined): BnBadgeVariant {
  switch (role) {
    case "owner":
      return "info";
    case "maintainer":
      return "success";
    default:
      return "neutral";
  }
}

/**
 * slugify derives a candidate URL label from a display name. It is a
 * convenience, not a validator: the field it fills stays editable and is checked
 * against [LABEL_RE] like any other input.
 */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, LABEL_MAX)
    .replace(/-+$/g, "");
}

/** fmtDate renders an API timestamp as a plain local date, or an em dash. */
function fmtDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/**
 * page-orgs is the organization list. It takes no properties — the route is
 * `/orgs` and the caller's memberships are the whole input.
 *
 *   <page-orgs></page-orgs>
 */
@customElement("page-orgs")
export class PageOrgs extends LitElement {
  @state() private orgs: Organization[] = [];
  @state() private loading = true;
  @state() private failure: ApiError | null = null;

  /* The create dialog. `createFailure` holds the typed error so the form can
     branch on `name_taken` and `quota_exceeded` instead of matching on prose. */
  @state() private showCreate = false;
  // These two are named for the WIRE, not for the labels above them: `name` is
  // the RFC-1123 label under the "URL label" field, `displayName` is the prose
  // under "Name". Spelling them the other way round is what made the create
  // form post a body the API refused, so the mapping is kept literal — a
  // `validation_failed` path from the server indexes these fields directly.
  @state() private name = "";
  @state() private displayName = "";
  @state() private nameTouched = false;
  @state() private creating = false;
  @state() private createFailure: ApiError | null = null;
  @state() private nameError = "";
  @state() private displayNameError = "";

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .head {
      display: flex;
      align-items: flex-start;
      flex-wrap: wrap;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-lg);
      font-weight: 600;
    }
    .lede {
      margin: 0.25rem 0 0;
      max-width: 62ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    .spacer {
      flex: 1 1 auto;
    }

    .org-cell {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
    }
    .org-cell a {
      font-weight: 600;
      color: var(--bino-text);
      text-decoration: none;
      border-radius: var(--bino-radius-sm);
    }
    .org-cell a:hover {
      color: var(--bino-active-text);
      text-decoration: underline;
    }
    .org-cell a:focus-visible {
      outline: none;
      box-shadow: var(--bino-focus-ring-shadow);
    }
    .slug {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .badges {
      display: inline-flex;
      align-items: center;
      gap: var(--bino-space-xs);
      flex-wrap: wrap;
    }
    .when {
      font-size: var(--bino-font-size-base);
      color: var(--bino-text-secondary);
      white-space: nowrap;
    }

    /* The failure block. It is deliberately not a bn-empty-state: "nothing here"
       and "we could not find out" are different answers, and only one of them
       carries a reference a support engineer can look up. */
    .failure {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-md);
      padding: var(--bino-space-sm) 0;
    }
    .failure h2 {
      margin: 0;
      font-size: var(--bino-font-size-md);
      font-weight: 600;
    }
    .failure p {
      margin: 0;
      max-width: 62ch;
      color: var(--bino-text-secondary);
      font-size: var(--bino-font-size-md);
    }
    .failure .actions {
      display: flex;
      gap: var(--bino-space-sm);
    }

    .quota {
      margin: 0 0 var(--bino-space-md);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    void this.load();
  }

  private async load() {
    this.loading = true;
    this.failure = null;
    try {
      const page = await listOrgs();
      this.orgs = page.items;
    } catch (err) {
      this.failure = parseError(err);
    } finally {
      this.loading = false;
    }
  }

  /* ── create ─────────────────────────────────────────────────────────── */

  private openCreate() {
    this.name = "";
    this.displayName = "";
    this.nameTouched = false;
    this.nameError = "";
    this.displayNameError = "";
    this.createFailure = null;
    this.showCreate = true;
  }

  private onDisplayName(value: string) {
    this.displayName = value;
    // The label tracks the name until the user takes it over, so the common case
    // is one decision rather than two. It stays editable either way.
    if (!this.nameTouched) this.name = slugify(value);
    this.displayNameError = "";
  }

  private onName(value: string) {
    this.name = value;
    this.nameTouched = true;
    this.nameError = "";
  }

  /**
   * validate checks the two fields the API will check anyway. It exists so a
   * typo costs no round trip, not to duplicate the server's authority — every
   * refusal below is re-rendered from the server's own `validation_failed`
   * `details.fields`.
   */
  private validate(): boolean {
    this.nameError = "";
    this.displayNameError = "";
    if (this.displayName.trim() === "") {
      this.displayNameError = "Enter a name for the organization.";
    }
    const label = this.name.trim();
    if (label === "") {
      this.nameError = "Enter a URL label.";
    } else if (label.length > LABEL_MAX) {
      this.nameError = `A URL label is at most ${LABEL_MAX} characters.`;
    } else if (!LABEL_RE.test(label)) {
      this.nameError =
        "Use lowercase letters, digits and hyphens, starting and ending with a letter or digit.";
    }
    return this.nameError === "" && this.displayNameError === "";
  }

  private async submitCreate() {
    if (this.creating) return;
    this.createFailure = null;
    if (!this.validate()) return;

    this.creating = true;
    try {
      const org = await createOrg({
        name: this.name.trim(),
        display_name: this.displayName.trim(),
      });
      toast(`Organization “${org.display_name || org.name}” created`, "success");
      this.showCreate = false;
      // In-app navigation, never a reload: `SC-S100` requires the new
      // organization to appear without one. A fresh organization has exactly one
      // member — the caller, as owner — so the detail screen is where the next
      // useful action lives.
      this.orgs = [...this.orgs, org];
      navigate(`/orgs/${encodeURIComponent(org.id)}`);
    } catch (err) {
      const failure = parseError(err);
      this.createFailure = failure;
      // `name_taken` is a field-level refusal: `SC-S100` requires the form to
      // keep what was typed, so the dialog stays open with the value intact.
      if (failure.code === "name_taken") {
        this.nameError = "That URL label is already in use. Choose a different one.";
      }
      // The field names are the wire's, so a server path indexes the form
      // directly — no translation table to drift out of step with the API.
      for (const f of validationFields(failure)) {
        const path = f.path.split(".").pop() ?? f.path;
        if (path === "name") this.nameError = f.reason;
        else if (path === "display_name") this.displayNameError = f.reason;
      }
    } finally {
      this.creating = false;
    }
  }

  /* ── rendering ──────────────────────────────────────────────────────── */

  /** supportRows renders the two facts a support ticket needs. The request id is
   * omitted rather than faked when the request never reached the server. */
  private supportRows(err: ApiError): BnKvRow[] {
    const rows: BnKvRow[] = [{ label: "Error code", value: err.code }];
    if (err.requestId) rows.push({ label: "Request id", value: err.requestId });
    return rows;
  }

  private renderFailure(heading: string, err: ApiError) {
    return html`<bn-card>
      <div class="failure" role="alert">
        <h2>${heading}</h2>
        <p>${err.message}</p>
        <bn-kv .rows=${this.supportRows(err)}></bn-kv>
        ${err.requestId
          ? nothing
          : html`<p>
              This request did not reach Sluice Cloud, so there is no reference to quote. Check your
              connection and try again.
            </p>`}
        <div class="actions">
          <bn-button variant="secondary" @click=${() => void this.load()}>Retry</bn-button>
        </div>
      </div>
    </bn-card>`;
  }

  private columns(): BnColumn[] {
    return [
      { key: "org", label: "Organization", width: "45%" },
      { key: "role", label: "Your role" },
      { key: "created", label: "Created" },
    ];
  }

  private rows(): BnRow[] {
    return this.orgs.map((o) => {
      const display = o.display_name || o.name;
      return {
        id: o.id,
        org: html`<span class="org-cell">
          <a href="/orgs/${encodeURIComponent(o.id)}">${display}</a>
          ${display !== o.name ? html`<span class="slug">${o.name}</span>` : nothing}
        </span>`,
        role: html`<span class="badges">
          <bn-badge .variant=${roleVariant(o.role)}
            >${o.role ? ROLE_LABELS[o.role].label : "unknown"}</bn-badge
          >
          ${o.personal
            ? html`<bn-badge
                variant="neutral"
                title="Created for you when you signed up. It always has exactly one owner: you."
                >personal</bn-badge
              >`
            : nothing}
        </span>`,
        created: html`<span class="when">${fmtDate(o.created)}</span>`,
      };
    });
  }

  private renderBody() {
    if (this.loading) {
      return html`<bn-spinner block size="28" label="Loading organizations"></bn-spinner>`;
    }
    if (this.failure) {
      return this.renderFailure("Could not load your organizations", this.failure);
    }
    if (this.orgs.length === 0) {
      // Reaching this is itself information: `SC-F101` creates a personal
      // organization with the account, so an empty list means the membership did
      // not land — say so instead of implying the reader forgot to make one.
      return html`<bn-card flush>
        <bn-empty-state
          icon="∅"
          heading="No organizations"
          message="Your account should already own a personal organization, created when you signed up. If this list stays empty, create one here and mention it when you contact support."
        >
          <bn-button slot="actions" variant="primary" @click=${() => this.openCreate()}
            >New organization</bn-button
          >
        </bn-empty-state>
      </bn-card>`;
    }
    return html`<bn-card flush>
      <bn-table
        caption="Organizations you belong to"
        .columns=${this.columns()}
        .rows=${this.rows()}
        empty="No organizations."
      ></bn-table>
    </bn-card>`;
  }

  /** renderQuota surfaces a `quota_exceeded` refusal as the consumption figure
   * it is (`SC-F901`), because "you are at 3 of 3" is actionable and "forbidden"
   * is not. */
  private renderQuota(err: ApiError) {
    const detail = err.details as unknown as QuotaDetail | undefined;
    if (!detail || typeof detail.limit !== "number") return nothing;
    return html`<p class="quota">
      Your plan allows ${detail.limit}
      ${detail.limit === 1 ? "organization" : "organizations"}, and you have ${detail.current}.
      Plan changes are not self-service — contact support to raise the limit.
    </p>`;
  }

  private renderCreateDialog() {
    const failure = this.createFailure;
    const showEnvelope =
      failure !== null && failure.code !== "name_taken" && failure.code !== "validation_failed";
    return html`<bn-modal
      ?open=${this.showCreate}
      heading="New organization"
      .width=${520}
      @close=${() => (this.showCreate = false)}
    >
      ${failure?.code === "quota_exceeded" ? this.renderQuota(failure) : nothing}
      ${showEnvelope
        ? html`<div class="failure" role="alert">
            <p>${failure.message}</p>
            <bn-kv .rows=${this.supportRows(failure)}></bn-kv>
          </div>`
        : nothing}

      <bn-field
        label="Name"
        required
        error=${this.displayNameError}
        hint="How this organization is named throughout Sluice Cloud. You will be its owner."
      >
        <bn-input
          .value=${this.displayName}
          placeholder="Acme Analytics"
          ?invalid=${this.displayNameError !== ""}
          @input=${(e: CustomEvent<{ value: string }>) => this.onDisplayName(e.detail.value)}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === "Enter") void this.submitCreate();
          }}
        ></bn-input>
      </bn-field>

      <bn-field
        label="URL label"
        required
        error=${this.nameError}
        hint="Lowercase letters, digits and hyphens. It appears in URLs and in generated instance hostnames."
      >
        <bn-input
          mono
          .value=${this.name}
          placeholder="acme-analytics"
          ?invalid=${this.nameError !== ""}
          @input=${(e: CustomEvent<{ value: string }>) => this.onName(e.detail.value)}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === "Enter") void this.submitCreate();
          }}
        ></bn-input>
      </bn-field>

      <bn-button slot="footer" variant="ghost" @click=${() => (this.showCreate = false)}
        >Cancel</bn-button
      >
      <bn-button
        slot="footer"
        variant="primary"
        ?loading=${this.creating}
        @click=${() => void this.submitCreate()}
        >Create organization</bn-button
      >
    </bn-modal>`;
  }

  override render() {
    return html`
      <div class="head">
        <div>
          <h1>Organizations</h1>
          <p class="lede">
            An organization owns instances, datasources, policy, audit and billing. Your personal
            organization was created with your account; create another to keep a company's resources
            separate from your own sandbox.
          </p>
        </div>
        <span class="spacer"></span>
        <bn-button variant="primary" @click=${() => this.openCreate()}>New organization</bn-button>
      </div>

      ${this.renderBody()} ${this.renderCreateDialog()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-orgs": PageOrgs;
  }
}
